import { I18nextProvider } from "react-i18next";
import { IntlProvider } from "react-intl";
import i18n from "../i18n";
import { useLocale } from "@/hooks";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client/react";
import { client } from "@/client";
import "@/styles/index.css";
import "react-toastify/dist/ReactToastify.css";
import { StrictMode } from "react";
import { ProtectedRoute } from "@/routing";
import { CreateTender, CreateQuickTender, Dashboard, ErrorFallback, Login, NotFound, Tenders, TokenLogin, AcceptInvite, ResetPassword } from "@/pages";
import { ThemeProvider } from "./context/ThemeProvider";
import { Role } from "./graphql/__generated__/graphql";
import { AdvancedCostParameters } from "./pages/Settings/AdvancedCostParameters";
import { EditTenderLayout, SettingsLayout, PreLoginLayout } from "./layouts";
import { MarginRulesComponent } from "./pages/Settings/MarginRules";
import { UsersComponent } from "./pages/Settings/Users";
import DetailsComponent from "./pages/EditTender/Details/DetailsComponent";
import Services from "./pages/EditTender/Services/ServicesTable";
import CarsTable from "./pages/EditTender/Cars/CarsTable";
import PurchasesTable from "./pages/EditTender/Purchases/PurchasesTable";
import { TooltipProvider } from "./components/ui/tooltip";
import { SessionProvider } from "./context/SessionProvider";
import AddMobileServiceComponent from "./pages/EditTender/AddService/Mobile/AddMobileServiceComponent";
import EditMobileServiceComponent from "./pages/EditTender/EditService/Mobile/EditMobileServiceComponent";
import AddPermanentServiceComponent from "./pages/EditTender/AddService/Permanent/AddPermanentServiceComponent";
import EditPermanentServiceComponent from "./pages/EditTender/EditService/Permanent/EditPermanentServiceComponent";
import BidRulesComponent from "./pages/Settings/BidRules/BidRulesComponent";
import BidDecisionsComponent from "./pages/EditTender/BidNoBid/BidDecisionsComponent";
import ExecutiveSummaryComponent from "./pages/EditTender/ExecutiveSummary/ExecutiveSummaryComponent";
import MarginDecisionsComponent from "./pages/EditTender/MarginDecisions/MarginDecisionsComponent";
import DocumentChatComponent from "./pages/EditTender/DocumentChat/DocumentChatComponent";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./queryClient";
import { DocumentProvider } from "./context/DocumentProvider";
import TasksBoard from "./pages/EditTender/TasksBoard/TasksBoard";
import MyTasks from "./pages/MyTasks/Page";
import { ErrorBoundary } from "react-error-boundary";
import DocumentsComponent from "./pages/EditTender/Documents/DocumentsComponent";
import ChatComponent from "./pages/EditTender/Chat/ChatComponent";
import { initVersionCheck } from "./utils/versionCheck";
import OptionsComponent from "./pages/EditTender/Options/OptionsComponent";
import SubmissionComponent from "./pages/EditTender/Submission/SubmissionComponent";
import { ToastContainer } from "react-toastify";
import AnalyticsComponent from "./pages/EditTender/Analytics/AnalyticsComponent";
import CreateRulePage from "./pages/Settings/RuleBuilderComponents/CreateRule/CreateRulePage";
import CalculationOverviewPage from "./pages/EditTender/CalculationOverview/CalculationOverviewPage";
import ConfigurationsComponent from "./pages/Settings/Configurations/ConfigurationsComponent";
import ConfigAdminsComponent from "./pages/Settings/ConfigAdmins/ConfigAdminsComponent";
import BillingsComponent from "./pages/Admin/Billing/BillingComponent";
import AdminLayout from "./layouts/AdminLayout";
import CreateOrganizationsPage from "./pages/Admin/Organizations/CreateOrganizationsPage";
import { BillingComponent } from "./pages/Settings/Billing";
import SubProtectedRoute from "./routing/SubProtectedRoute";
import OrganizationsComponent from "./pages/Admin/Organizations/OrganizationsComponent";
import SheetAgentPage from "./pages/EditTender/SheetAgent/SheetAgentPage";
import SheetAgentRunPage from "./pages/EditTender/SheetAgent/SheetAgentRunPage/SheetAgentRunPage";
import NotificationsComponent from "./pages/Settings/Notifications/NotificationsComponent";

const isDevelopment = import.meta.env.VITE_APP_ENV === "dev";

function App() {

  initVersionCheck();

  // Hooks
  const currentLocale = useLocale();
  const AppContent = (
    <>
      <ToastContainer position="top-center" autoClose={2000} />
      <ApolloProvider client={client}>
        <QueryClientProvider client={queryClient}>
          <IntlProvider locale={currentLocale}>
            <I18nextProvider i18n={i18n}>
              <ThemeProvider>
                <TooltipProvider>
                  <DocumentProvider>
                    <BrowserRouter >
                      <SessionProvider>
                        <ErrorBoundary fallback={<ErrorFallback />}>
                          <Routes>
                            {/* Protected routes */}
                            <Route element={<ProtectedRoute roles={Object.values(Role)} />}>
                              <Route path="tender/" element={<Outlet />}>
                                <Route path="all" element={<Tenders />} />
                                <Route path="new" element={<CreateTender />} />
                                <Route path="newquickchance" element={<CreateQuickTender />} />
                                <Route path=":tenderID/" element={<EditTenderLayout />}>
                                  <Route index element={<Navigate to="details" replace />} />
                                  <Route path="documentchat" Component={DocumentChatComponent} />
                                  <Route path="executivesummary" Component={ExecutiveSummaryComponent} />
                                  <Route path="details" Component={DetailsComponent} />
                                  <Route path="documents" Component={DocumentsComponent} />
                                  <Route path="analytics" Component={AnalyticsComponent} />
                                  <Route path="calculation-overview" Component={CalculationOverviewPage} />
                                  <Route path="services">
                                    <Route path="all" Component={Services} />
                                    <Route path="permanent">
                                      <Route path="new" Component={AddPermanentServiceComponent} />
                                      <Route path=":serviceID" Component={EditPermanentServiceComponent} />
                                    </Route>
                                    <Route path="mobile">
                                      <Route path="new" Component={AddMobileServiceComponent} />
                                      <Route path=":serviceID" Component={EditMobileServiceComponent} />
                                    </Route>
                                  </Route>
                                  <Route path="cars" Component={CarsTable} />
                                  <Route path="purchases" Component={PurchasesTable} />
                                  <Route path="biddecisions">
                                    <Route path=":bidDecisionGroupID" Component={BidDecisionsComponent} />
                                  </Route>
                                  <Route path="margindecisions">
                                    <Route path=":marginDecisionGroupID" Component={MarginDecisionsComponent} />
                                  </Route>
                                  <Route path="sheet-agent">
                                    <Route index Component={SheetAgentPage} />
                                    <Route path=":sheetAgentRunId" Component={SheetAgentRunPage} />
                                  </Route>
                                  <Route path="chat" Component={ChatComponent} />
                                  <Route path="tasksboard" Component={TasksBoard} />
                                  <Route path="options" Component={OptionsComponent} />
                                  <Route path="submission" Component={SubmissionComponent} />
                                </Route>
                              </Route>
                              <Route path="dashboard" element={<Dashboard />} />
                              <Route path="mytasks" element={<MyTasks />} />
                              <Route path="settings/" element={<SettingsLayout />}>
                                <Route index element={<Navigate to="notifications/" replace />} />
                                <Route path="notifications/" element={<NotificationsComponent />} />
                                <Route path="bidrules/">
                                  <Route index element={<BidRulesComponent />} />
                                  <Route path="newrule" element={<CreateRulePage />} />
                                </Route>
                                <Route path="bidrules/">
                                  <Route index element={<BidRulesComponent />} />
                                  <Route path="newrule" element={<CreateRulePage />} />
                                </Route>
                                <Route path="marginrules/">
                                  <Route index element={<MarginRulesComponent />} />
                                  <Route path="newrule" element={<CreateRulePage />} />
                                </Route>
                                <Route path="users/" element={<UsersComponent />} />
                                <Route path="advancedcostparameters/" element={<AdvancedCostParameters />} />
                                <Route path="configurations/" element={<ConfigurationsComponent />} />
                                <Route path="configadmins/" element={<ConfigAdminsComponent />} />
                                <Route element={<SubProtectedRoute roles={[Role.Admin, Role.Manager]} fallbackRoute="/settings" />}>
                                  <Route path="billing/" element={<BillingComponent />} />
                                </Route>
                              </Route>
                            </Route>
                            {/* Admin routes */}
                            <Route element={<ProtectedRoute roles={[Role.Admin]} />}>
                              <Route path="admin" element={<AdminLayout />}>
                                <Route index element={<Navigate to="billings" replace />} />
                                <Route path="billings" element={<BillingsComponent />} />
                                <Route path="organizations" element={<OrganizationsComponent />} />
                                <Route path="organizations/create" element={<CreateOrganizationsPage />} />
                              </Route>
                            </Route>
                            {/* Unprotected routes */}
                            <Route path="/" element={<Navigate to="/login" replace />} />
                            <Route path="*" element={<NotFound />} />
                            {/* Pre-login routes with shared layout */}
                            <Route path="/" element={<PreLoginLayout />}>
                              <Route path="login" element={<Login />} />
                              <Route path="/verify-token/:token" element={<TokenLogin />} />
                              {/* Route to handle missing token */}
                              <Route path="/verify-token" element={<Navigate to="/login" replace />} />
                              <Route path="/accept-invite/:sessionID" element={<AcceptInvite />} />
                              {/* Route to handle missing sessionID */}
                              <Route path="/accept-invite" element={<Navigate to="/login" replace />} />
                              <Route path="/reset-password/:token" element={<ResetPassword />} />
                              {/* Route to handle missing token */}
                              <Route path="/reset-password" element={<ResetPassword />} />
                            </Route>
                          </Routes>
                        </ErrorBoundary>
                      </SessionProvider>
                    </BrowserRouter>
                  </DocumentProvider>
                </TooltipProvider>
              </ThemeProvider>
            </I18nextProvider>
          </IntlProvider>
        </QueryClientProvider>
      </ApolloProvider>
    </>
  );

  return isDevelopment ? <StrictMode>{AppContent}</StrictMode> : AppContent;
}

export default App;
